/**
 * TreeMap Organism Component
 *
 * A columnar visualization for hierarchical sphere structures with:
 * - Context-driven hierarchy definition
 * - Drag-and-drop type conversion
 * - AI-assisted generation
 * - Navigation and drill-down
 * - Swimlane pagination
 */

import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { fetchArcTemplate } from '@center-pwa/reducers/arcTemplatesReducer';
import { Toast } from 'primereact/toast';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import CustomDialog from '../../atoms/CustomDialog/CustomDialogAtom';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import AddTemplate from '../../molecules/AddTemplate/AddTemplate';
import AddSphere from '../../molecules/AddSphere/AddSphere';
import EditSphere from '../../molecules/Atlas/EditSphere/EditSphere';

import TreeMapColumn from '../../molecules/TreeMapColumn/TreeMapColumn';
import TreeMapBreadcrumbs from '../../molecules/TreeMapBreadcrumbs/TreeMapBreadcrumbs';
import TreeMapAIAssistant from '../../molecules/TreeMapAIAssistant/TreeMapAIAssistant';
import TreeMapCard from '../../molecules/TreeMapCard/TreeMapCard';
import TreeMapGrid from './TreeMapGrid';

import { useAvailableTypes } from '../../../hooks/useAvailableTypes';
import { useAspectCatalog } from '../../../hooks/useAspectCatalog';
import { useTreeMap } from './useTreeMap';
import { useTreeMapLayout } from './useTreeMapLayout';
import type { TreeMapProps, CardView, Sphere as TreeMapSphere, ContextLevel } from './types';
import {
  expandContextLevels,
  resolveEffectivePageSize,
  pruneCollapsedNodeIds,
  capCollapsedNodeIds,
  getLevelIdForColumn,
} from './defaults';
import type { Sphere as GlobalSphere } from 'types/global';
import type { TreeMapColumnState } from 'types/contextView';
import type { Schema } from 'types/schema';
import { fetchPermissionList, getSphereById } from '@center-platform/spheres/sphere';
import { getEligibleSchemaObjects } from '@center-platform/spheres/identity';
import type { PermissionList } from 'types/permissions';
import type { PropertyOption } from '../../molecules/PropertyFilter';
import type { PerspectiveOption } from '../../molecules/PerspectiveFilter';
import type { RelationshipOption } from '../../molecules/RelationshipFilter';
import {
  buildFilterPerspectiveOptions,
  buildFilterPropertyOptions,
  buildFilterRelationshipOptions,
  type SchemaForFilters,
} from '../../../utils/filterPropertyBuilder';
import { urgencyLevels } from '../../../constants';

import './TreeMap.scss';

/**
 * Helper: Translate Surface filter terminology to Schema terminology
 * Surface uses: "type", "label"
 * Schemas use: "system_name", "node_label"
 */
const translateFilter = (filter: any) => {
  if (filter.property === 'type') {
    return { ...filter, property: 'system_name' };
  }
  if (filter.property === 'label') {
    return { ...filter, property: 'node_label' };
  }
  // Other properties don't apply to schemas, filter them out
  return null;
};

/**
 * Helper: Check if a single filter matches a schema
 */
const checkFilterMatch = (schema: any, filter: any) => {
  let value;
  if (filter.property === 'system_name' || filter.property === 'node_label') {
    value = schema[filter.property];
  } else {
    const prop = schema.properties?.find((p: any) => p.name === filter.property);
    if (!prop) return false;
    value = prop.default_value;
  }
  // Handle different operators
  if (filter.operator === 'equals') {
    return Array.isArray(filter.values) ? filter.values.includes(value) : value === filter.value;
  }
  if (filter.operator === 'in') {
    return Array.isArray(filter.values) ? filter.values.includes(value) : false;
  }
  return true; // For now, only handle equals and in
};

/**
 * Main Tree Map Component
 */
export const TreeMap: React.FC<TreeMapProps> = ({
  context,
  viewConfig,
  baseConfig,
  organizationId,
  rootSphereId,
  editable = true,
  onNodeSelect,
  onNodeCreate,
  onNodeUpdate,
  onNodeDelete,
  initialCardView = 'standard',
  showBreadcrumbs = true,
  showFilters = true,
  showAI = true,
  defaultPageSize = 5,
  useGridLayout = false, // Feature flag for new grid-based layout
  levelFilters,
  runtimeFilters,
  filterSaveTargets = [],
  onSetLevelFilter,
  linkSuppressed,
  onExitSharedLink,
  canEditContext = false,
  onEditContext,
  onShareWithView,
  className = '',
  style,
  contextBar,
  onCardViewChange,
  onBoardStateChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiClients = useApiClientsOrNull();
  const toastRef = useRef<Toast>(null);

  // Schema types available for CREATION in this tree's scope (approaches
  // deployed on the root sphere + ancestors). Drives the add-sphere flow and
  // arc-template prefetch. Also warms the schemaTypes demand cache for
  // downstream point lookups.
  //
  // NOT a filter catalog. This answers "what can I create here?", and a sphere
  // with no deployed approaches legitimately yields zero types -- which is why
  // the filter picker must not be built from it (see orgFilterSchemas below).
  const { schemas } = useAvailableTypes(apiClients, rootSphereId);

  // Org-wide type catalog for the FILTER pickers. Filtering asks a different
  // question than creation: which aspects exist on the types a level can
  // CONTAIN, regardless of what is deployable on this particular sphere.
  //
  // EAINT-781: the level filter picker used to be built from `schemas` above.
  // On a root sphere with no deployed approaches that list is empty, so the
  // picker rendered "No aspects match" with no error -- the request had
  // succeeded and correctly returned nothing. Same source as ContextEditorPage,
  // so the tree map and the context editor now answer "what can I filter on?"
  // identically.
  const {
    orgFilterSchemas,
    propertyOptions: orgWideFilterProperties,
    loading: orgFilterSchemasLoading,
    error: orgFilterSchemasError,
  } = useAspectCatalog(apiClients);
  const arcTemplates = useSelector((state: any) => {
    const templates = state.arcTemplates || {};
    console.log('[TreeMap] Redux arcTemplates selector:', {
      hasArcTemplates: !!templates,
      hasTemplates: !!templates.templates,
      templateKeys: Object.keys(templates.templates || {}),
      fullState: templates,
    });
    return templates;
  });

  // `viewConfig`/`baseConfig` arrive already resolved (defaults → view → user →
  // session — see `resolveViewConfig` / `TreeMapOrganism`), so the density
  // toggle's live selection is already reflected here with no local mirror:
  // `onCardViewChange` writes the session layer directly and the next render
  // sees it through `viewConfig.defaultCardView`.
  const cardView: CardView = viewConfig.defaultCardView ?? baseConfig.cardDensity ?? initialCardView;

  // Backward-compatible template shim for useTreeMapLayout and rendering code
  // (will be removed when those consumers are updated in Phase 16.5+)
  const templateShim = React.useMemo(() => ({
    system_name: context.system_name,
    name: context.name?.default || context.name?.translations?.['en-US'] || context.system_name,
    levels: context.definition?.levels || [],
  }), [context.system_name, context.name, context.definition?.levels]);

  // Levels expanded for `repeat`, in column order (index 0 = column 1, the
  // first level after Focus). Hoisted above the board-state hooks below since
  // hydrating hiddenColumns from viewConfig.columnState needs a column→levelId
  // map before the component's first render.
  const contextLevels = context?.definition?.levels;
  const columnsToRender = React.useMemo(() => {
    return expandContextLevels(contextLevels || []);
  }, [contextLevels]);

  // View-level page size wins over the defaultPageSize prop.
  const effectivePageSize = resolveEffectivePageSize(viewConfig, defaultPageSize);

  // Custom hook for business logic
  const {
    spheresByColumn,
    paginationState,
    sphereLookup,
    navigation,
    drillDown,
    focusOnSphere,
    drillDownColumn,
    navigateToBreadcrumb,
    moveSphere,
    acceptSuggestion,
    loadMoreChildren,
    updateSphereInState,
    addSphereToState,
    pollForUpdates,
    refreshSpheres,
    isLoading,
    hasLoaded,
    error,
    // Destructured but not yet used - available for future implementation:
    // navigateBack, createSphere, updateSphere, deleteSphere, convertSphere,
    // canConvert, getConversionRules, getSuggestions, refreshLevel
  } = useTreeMap({
    context,
    viewConfig,
    baseConfig,
    rootSphereId,
    organizationId,
    defaultPageSize: effectivePageSize,
    cardView,
    runtimeFilters,
  });

  // Reorganize spheres if we've drilled down to a different root.
  // Column hiding is not decided here: when focusing, the reorganized
  // structure IS the correct view (see below), and level visibility now comes
  // from the resolved config's `columnState`.
  const { reorganizedSpheres, rootTemplateLevel } = React.useMemo(() => {
    // Skip reorganization if we don't have the required data yet
    if (!navigation.rootSphereId || !context) {
      return { reorganizedSpheres: spheresByColumn, rootTemplateLevel: 0 };
    }

    // Skip reorganization if spheresByColumn is empty (data not loaded yet)
    const hasData = Object.keys(spheresByColumn).length > 0 &&
                    Object.values(spheresByColumn).some(spheres => spheres.length > 0);

    if (!hasData) {
      return { reorganizedSpheres: spheresByColumn, rootTemplateLevel: 0 };
    }

    // Find the focused sphere across all columns and track its original column
    let focusedSphere: TreeMapSphere | null = null;
    let originalColumn: number | null = null;

    for (const [colStr, spheres] of Object.entries(spheresByColumn)) {
      const found = spheres.find(s => s.id === navigation.rootSphereId);
      if (found) {
        focusedSphere = found;
        originalColumn = Number.parseInt(colStr, 10);
        break;
      }
    }

    if (!focusedSphere || originalColumn === null) {
      console.warn('⚠️ [TreeMap] Could not find focused sphere in data');
      return { reorganizedSpheres: spheresByColumn, rootTemplateLevel: 0 };
    }

    // Reorganize: focused sphere becomes column 0, its descendants shift down
    const newSpheresByColumn: Record<number, TreeMapSphere[]> = {};

    // Column 0: just the focused sphere
    newSpheresByColumn[0] = [focusedSphere];

    // Collect all descendants recursively
    const allSpheres = Object.values(spheresByColumn).flat();

    const getDescendants = (parentId: string, level: number) => {
      const children = allSpheres.filter(s => s.parent_id === parentId);
      if (children.length > 0) {
        // Accumulate children at this level (don't overwrite!)
        if (!newSpheresByColumn[level]) {
          newSpheresByColumn[level] = [];
        }
        newSpheresByColumn[level].push(...children);

        // Recurse for each child's descendants
        children.forEach(child => getDescendants(child.id, level + 1));
      }
    };

    getDescendants(focusedSphere.id, 1);

    // When focusing, DON'T hide any columns - the reorganized structure IS the correct view
    // Column 0 = focused sphere, columns 1+ = all its descendants at appropriate levels
    // If originalColumn is -1 (the actual root sphere), treat it as 0 for template level calculations
    const effectiveTemplateLevel = originalColumn === -1 ? 0 : originalColumn;
    return { reorganizedSpheres: newSpheresByColumn, rootTemplateLevel: effectiveTemplateLevel };
  }, [spheresByColumn, navigation.rootSphereId, context]);

  /** Resolves a column index to its `columnState` key; `null` when the column maps to no level. */
  const resolveLevelIdForColumn = React.useCallback(
    (column: number): string | null => getLevelIdForColumn(columnsToRender, rootTemplateLevel, column),
    [columnsToRender, rootTemplateLevel]
  );

  // Rendered hidden-column set, derived straight from the resolved
  // `viewConfig.columnState` — hidden columns are tracked by LEVEL ID there,
  // never by column index (drilling down changes rootTemplateLevel, which
  // shifts which level each column index maps to), so a level stays hidden
  // wherever it renders instead of the position it happened to occupy. No
  // local mirror: a session-layer write (or a Reset that clears it) is
  // reflected on the very next render.
  const hiddenColumns = React.useMemo<Set<number>>(() => {
    const columns = new Set<number>();
    const totalColumns = columnsToRender.length - rootTemplateLevel + 1; // +1 for Focus column
    for (let col = 0; col < totalColumns; col++) {
      const levelId = getLevelIdForColumn(columnsToRender, rootTemplateLevel, col);
      if (levelId && viewConfig.columnState?.[levelId]?.hidden) columns.add(col);
    }
    return columns;
  }, [viewConfig.columnState, columnsToRender, rootTemplateLevel]);

  // Grid layout computation (only if useGridLayout enabled)
  const { layout } = useTreeMapLayout({
    spheresByColumn: reorganizedSpheres,
    template: templateShim as any,
    rootTemplateLevel,
  });

  // Additional local UI state
  const [aiDialogVisible, setAIDialogVisible] = React.useState(false);
  const [aiContext, setAIContext] = React.useState<any>(null);
  const [selectedSphereIds, setSelectedSphereIds] = React.useState<string[]>([]);

  // Reflects a background reload (level-filter change, cardView bump, etc.)
  // across every rendered column — mirrors the pre-EAINT-781 behaviour, which
  // always marked the whole hierarchy loading rather than one column, since a
  // filter change re-executes the entire tree query. Not shown on the very
  // first load — that's the full-page loading state returned further below.
  // Keyed on `hasLoaded`, not on whether the board holds spheres: a reload over
  // an empty result is still a reload, and the columns (with any open column
  // filter popover) must stay mounted through it.
  const loadingColumns = React.useMemo<Set<number>>(() => {
    if (!isLoading || !hasLoaded) return new Set();
    const totalColumns = columnsToRender.length - rootTemplateLevel + 1; // +1 for Focus column
    const columns = new Set<number>();
    for (let i = 0; i <= totalColumns; i++) columns.add(i);
    return columns;
  }, [isLoading, hasLoaded, columnsToRender, rootTemplateLevel]);

  // Render-time collapsed set: derived straight from the resolved
  // `viewConfig.collapsedNodeIds` (no local mirror — see `cardView` above for
  // why). Stale ids (spheres no longer in the current result set) are dropped
  // here only — the stored list itself keeps them so a sphere that reappears
  // after a filter change is still collapsed.
  const currentResultSetIds = React.useMemo(() => {
    const ids = new Set<string>();
    Object.values(reorganizedSpheres).forEach((spheres) => {
      (spheres as TreeMapSphere[]).forEach((s) => ids.add(s.id));
    });
    return ids;
  }, [reorganizedSpheres]);

  const renderCollapsedNodeIds = React.useMemo(
    () => pruneCollapsedNodeIds(viewConfig.collapsedNodeIds, currentResultSetIds),
    [viewConfig.collapsedNodeIds, currentResultSetIds]
  );

  // Sphere creation dialog state
  const [templateDialogVisible, setTemplateDialogVisible] = React.useState(false);
  const [addSphereDialogVisible, setAddSphereDialogVisible] = React.useState(false);
  const [parentSphereForCreation, setParentSphereForCreation] = React.useState<TreeMapSphere | null>(null);
  const [childColumnConfig, setChildColumnConfig] = React.useState<ContextLevel | null>(null);
  const [selectedSchema, setSelectedSchema] = React.useState<Schema | null>(null);

  // Sphere editing dialog state
  const [editSphereDialogVisible, setEditSphereDialogVisible] = React.useState(false);
  const [sphereToEdit, setSphereToEdit] = React.useState<TreeMapSphere | null>(null);
  const [parentSphereForEdit, setParentSphereForEdit] = React.useState<TreeMapSphere | null>(null);

  // Track newly created sphere IDs to show "new" indicator
  const [newSphereIds, setNewSphereIds] = React.useState<Set<string>>(new Set());

  // Show error toasts
  useEffect(() => {
    if (error && toastRef.current) {
      toastRef.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 5000,
      });
    }
  }, [error]);

  // Fetch flow templates for schemas that have flows
  useEffect(() => {
    console.log('[TreeMap useEffect] Flow template fetch check:', {
      hasApiClients: !!apiClients,
      hasSchemas: !!schemas,
      schemasLength: schemas?.length,
    });

    if (!apiClients || !schemas || schemas.length === 0) return;

    // Get unique flow system names from all schemas
    const flowSystemNames = new Set<string>();
    schemas.forEach((schema: any) => {
      if (schema.flow) {
        flowSystemNames.add(schema.flow);
        console.log('[TreeMap useEffect] Found schema with flow:', {
          schemaName: schema.system_name,
          flowSystemName: schema.flow,
        });
      }
    });

    console.log('[TreeMap useEffect] Dispatching fetchArcTemplate for:', Array.from(flowSystemNames));

    // Dispatch fetch for each flow template (thunk has built-in deduplication)
    flowSystemNames.forEach((systemName) => {
      dispatch(fetchArcTemplate({ systemName, apiClient: apiClients.schemaClient }) as any);
    });
  }, [schemas, apiClients, dispatch]);

  // Handle sphere selection
  const handleSphereSelect = (sphereId: string) => {
    setSelectedSphereIds([sphereId]);
    onNodeSelect?.(sphereId);
  };

  // Handle drill-down
  const handleExpand = (sphereId: string) => {
    drillDown(sphereId);
  };

  // Handle column expansion
  const handleColumnExpand = (columnLevel: number) => {
    drillDownColumn(columnLevel);
  };

  // Handle AI suggestion acceptance
  const handleSuggestionAccept = async (suggestion: any) => {
    try {
      const newSphere = await acceptSuggestion(aiContext.parentSphere.id, suggestion);
      onNodeCreate?.(newSphere);
      setAIDialogVisible(false);
      toastRef.current?.show({
        severity: 'success',
        summary: 'Sphere Created',
        detail: `Created "${suggestion.name}" successfully`,
        life: 3000,
      });
    } catch (err: any) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Creation Failed',
        detail: err.message || 'Failed to create sphere',
        life: 5000,
      });
    }
  };

  // Handle node collapse/expand.
  //
  // Known exception to the partials-only invariant (SC3). The seed is the
  // RESOLVED list, so the first toggle copies whatever the shared view already
  // collapsed into the user's own overlay; from then on this field is a fork
  // and stops tracking later changes to the shared view. That is inherent to a
  // whole-value array field — there is no per-element delta to send — and it is
  // why only `columnState` / `levelOverrides` (true `Record<levelId, …>` fields)
  // get per-key merge treatment. Documented in the spec's Edge Cases table.
  const handleToggleCollapse = (nodeId: string) => {
    const newSet = new Set(viewConfig.collapsedNodeIds ?? []);
    if (newSet.has(nodeId)) {
      newSet.delete(nodeId);
    } else {
      newSet.add(nodeId);
    }
    onBoardStateChange?.({ collapsedNodeIds: capCollapsedNodeIds(Array.from(newSet)) });
  };

  /**
   * Get ancestor IDs for a given sphere by traversing parent_id chain.
   * Used for polling after mutations to catch status cascades.
   */
  const getAncestorIds = React.useCallback(
    (sphereId: string): string[] => {
      const ancestors: string[] = [];
      let currentId = sphereId;

      // Traverse parent_id chain through spheresByColumn
      while (currentId) {
        // Find the sphere in any column
        let foundSphere: TreeMapSphere | undefined;
        for (const spheres of Object.values(spheresByColumn)) {
          foundSphere = spheres.find((s) => s.id === currentId);
          if (foundSphere) break;
        }

        if (!foundSphere?.parent_id || ancestors.includes(foundSphere.parent_id)) {
          break; // No parent or cycle detected
        }

        ancestors.push(foundSphere.parent_id);
        currentId = foundSphere.parent_id;
      }

      return ancestors;
    },
    [spheresByColumn]
  );

  // Handle column hide/show
  // When hiding: hide the clicked column AND all columns to the right
  // When showing: show all columns (clear hidden state)
  const handleToggleColumnVisibility = (columnIndex: number) => {
    const currentlyHiddenLevelIds = new Set(
      Object.entries(viewConfig.columnState ?? {})
        .filter(([, state]) => state?.hidden)
        .map(([levelId]) => levelId)
    );

    // If this column is already hidden, unhide all columns (show everything)
    if (hiddenColumns.has(columnIndex)) {
      const columnStatePatch: Record<string, TreeMapColumnState> = {};
      currentlyHiddenLevelIds.forEach((levelId) => {
        columnStatePatch[levelId] = { hidden: false };
      });
      onBoardStateChange?.({ columnState: columnStatePatch });
      return;
    }

    // Otherwise, hide this column and all columns to the right.
    // The bound comes from the *expanded* level list (post-`repeat`), not
    // `context.definition.levels` — a level with `repeat > 1` renders as several
    // columns, so the raw list understates the real count and would persist only
    // a prefix of the hidden set.
    //
    // Note the `repeat` consequence, which is spec-accepted (composite
    // `levelId#repeatIndex` keys are out of scope): all N columns produced by one
    // repeated level share a single hidden flag, so hiding any hop hides every
    // hop of that level. That is applied immediately here rather than only on
    // reload, so what the user sees is what gets restored.
    const totalColumns = columnsToRender.length - rootTemplateLevel + 1; // +1 for Focus column
    const columnStatePatch: Record<string, TreeMapColumnState> = {};

    for (let i = columnIndex; i < totalColumns; i++) {
      const levelId = resolveLevelIdForColumn(i);
      if (levelId && !currentlyHiddenLevelIds.has(levelId)) {
        currentlyHiddenLevelIds.add(levelId);
        columnStatePatch[levelId] = { hidden: true };
      }
    }

    if (Object.keys(columnStatePatch).length > 0) {
      onBoardStateChange?.({ columnState: columnStatePatch });
    }
  };

  // Handle sphere creation - smart schema selection
  const handleNodeCreate = async (clickedSphere: TreeMapSphere) => {
    // Early return if apiClients is null
    if (!apiClients) {
      console.error('❌ [TreeMap] API clients not available');
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'API clients not initialized. Please refresh the page.',
        life: 5000,
      });
      return;
    }

    // The clicked sphere IS the parent - we're adding a child to it
    // Fetch it with assignedPerspectives so buildPerspectiveAssignments can work
    try {
      const response = await getSphereById(
        apiClients.graphClient,
        clickedSphere.id,
        true, // getAssignedPerspectives
        false // skipCache
      );

      // The response is wrapped in { success, statusCode, message, data }
      // and data contains { sphere: {...} }
      // IMPORTANT: Preserve the __columnIndex from the clicked sphere
      const fullParentSphere: any = {
        ...(response.data?.sphere as TreeMapSphere),
        __columnIndex: clickedSphere.__columnIndex,
      };

      // Use the full parent sphere for the rest of the logic
      const parentSphereToUse = fullParentSphere;

      // Get the column index from the clicked sphere (passed from TreeMapGrid)
      const parentColumn = parentSphereToUse.__columnIndex;

      // The child goes in the column AFTER the parent (children of the parent sphere)
      // E.g., Parent in Focus (0) → Child in Pillars (1)
      //       Parent in Pillars (1) → Child in Priorities (2)
      const targetColumn = parentColumn + 1;

      // When focused on a sphere, we need to map the reorganized column index back to the original template level
      // In root view: rootTemplateLevel = 0, so targetColumn - 1 gives the correct template index
      // In focused view: rootTemplateLevel = original column of focused sphere
      //   Example: focused on Priority (rootTemplateLevel=2), column 1 = Strategies = contextLevels[2]
      //   Formula: contextLevels[rootTemplateLevel + targetColumn - 1]
      const contextLevels = context?.definition?.levels || [];
      const templateLevelIndex = rootTemplateLevel + targetColumn - 1;
      const targetColumnFilters = typeof targetColumn === 'number' && targetColumn > 0 && templateLevelIndex < contextLevels.length
        ? contextLevels[templateLevelIndex]?.filters
        : undefined;

      console.log('🔍 [AddTemplate] Target column:', targetColumn, 'Template level index:', templateLevelIndex);
      console.log('🔍 [AddTemplate] Target column filters:', targetColumnFilters);
      console.log('🔍 [AddTemplate] Has filterGroups?', !!targetColumnFilters?.filterGroups, 'Count:', targetColumnFilters?.filterGroups?.length);

      const translatedPropertyFilters = targetColumnFilters?.propertyFilters?.map(translateFilter).filter((f: any) => f !== null);

      // Translate filterGroups if present
      const translatedFilterGroups = targetColumnFilters?.filterGroups?.map((group: any) => ({
        ...group,
        filters: group.filters.map(translateFilter).filter((f: any) => f !== null)
      }));

      // Check how many schemas are eligible before showing the dialog
      const perspectiveSystemNames = Array.isArray(parentSphereToUse.perspectives)
        ? parentSphereToUse.perspectives.map((p: any) => p.properties.system_name)
        : Object.keys(parentSphereToUse.perspectives || {});

      let eligibleSchemas = getEligibleSchemaObjects(schemas, {
        assigneeSystemName: parentSphereToUse.properties.type,
        // The parent's own type usually isn't among the available (provided) types,
        // so its schema isn't in `schemas`. Pass its node_label (the sphere's Gremlin
        // vertex label) directly so getEligibleSchemaObjects can match label-based
        // perspectives instead of bailing to [] (which would wrongly warn
        // "No Valid Types" and never open the dialog).
        assigneeNodeLabel: parentSphereToUse.label,
        ...(perspectiveSystemNames.length > 0
          ? { perspectiveSystemNames, enforcePerspectiveMatch: true }
          : {}),
      });

      // Apply the translated property filters (AND logic)
      if (translatedPropertyFilters && translatedPropertyFilters.length > 0) {
        eligibleSchemas = eligibleSchemas.filter((schemaResult: any) => {
          const schema = schemaResult.schema || schemaResult;
          return translatedPropertyFilters.every((filter: any) => checkFilterMatch(schema, filter));
        });
      }

      // Apply filterGroups (supports OR/AND logic)
      if (translatedFilterGroups && translatedFilterGroups.length > 0) {
        console.log('🔍 [AddTemplate] Applying filterGroups, schemas before:', eligibleSchemas.length);
        eligibleSchemas = eligibleSchemas.filter((schemaResult: any) => {
          const schema = schemaResult.schema || schemaResult;
          // All filter groups must pass (AND between groups)
          const passes = translatedFilterGroups.every((group: any) => {
            if (group.operator === 'or') {
              // At least one filter in the group must match
              const result = group.filters.some((filter: any) => {
                const match = checkFilterMatch(schema, filter);
                console.log(`  Schema ${schema.system_name} - Filter ${filter.property}=${filter.values?.join(',')}: ${match ? '✓' : '✗'}`);
                return match;
              });
              console.log(`  Schema ${schema.system_name} - Group OR result: ${result ? '✓ PASS' : '✗ FAIL'}`);
              return result;
            } else {
              // All filters in the group must match (AND)
              return group.filters.every((filter: any) => checkFilterMatch(schema, filter));
            }
          });
          return passes;
        });
        console.log('🔍 [AddTemplate] After filterGroups, schemas remaining:', eligibleSchemas.length);
        console.log('🔍 [AddTemplate] Remaining schema types:', eligibleSchemas.map((s: any) => (s.schema || s).system_name));
      }

      // If only 1 schema is eligible, skip the template dialog and go straight to AddSphere
      if (eligibleSchemas.length === 1) {
        setParentSphereForCreation(parentSphereToUse);
        setSelectedSchema(eligibleSchemas[0].schema);
        setAddSphereDialogVisible(true);
        return;
      }

      // If 0 schemas, show error
      if (eligibleSchemas.length === 0) {
        toastRef.current?.show({
          severity: 'warn',
          summary: 'No Valid Types',
          detail: `No sphere types are available for ${parentSphereToUse.properties.name}.`,
          life: 5000,
        });
        return;
      }

      // Otherwise, show the template dialog
      setParentSphereForCreation(parentSphereToUse);
      setChildColumnConfig((translatedPropertyFilters && translatedPropertyFilters.length > 0) || (translatedFilterGroups && translatedFilterGroups.length > 0)
        ? {
            filters: {
              ...(translatedPropertyFilters && translatedPropertyFilters.length > 0 ? { propertyFilters: translatedPropertyFilters } : {}),
              ...(translatedFilterGroups && translatedFilterGroups.length > 0 ? { filterGroups: translatedFilterGroups } : {}),
            }
          } as any
        : null);
      setTemplateDialogVisible(true);
    } catch (error: any) {
      console.error('❌ [TreeMap] Failed to fetch full parent sphere:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load parent sphere data. Please try again.',
        life: 5000,
      });
    }
  };

  // Handle sphere edit - open EditSphere dialog
  const handleNodeUpdateInternal = (sphere: TreeMapSphere) => {
    // Find parent sphere from spheresByColumn
    const parentSphere = Object.values(spheresByColumn)
      .flat()
      .find((s: TreeMapSphere) => s.id === sphere.parent_id);

    if (!parentSphere) {
      console.error('[TreeMap] Parent sphere not found for editing');
      return;
    }

    setSphereToEdit(sphere);
    setParentSphereForEdit(parentSphere);
    setEditSphereDialogVisible(true);
  };

  // Handle schema selection from AddTemplate
  const handleSchemaSelect = (schema: Schema) => {
    setSelectedSchema(schema);
    setTemplateDialogVisible(false);
    setAddSphereDialogVisible(true);
  };

  // Handle sphere save from AddSphere
  // Note: AddSphere handles the API call internally, we just receive the created sphere
  const handleSphereSave = (createdSphere: GlobalSphere, parentId: string) => {
    // Convert GlobalSphere to TreeMapSphere format
    const treeMapSphere: TreeMapSphere = {
      id: createdSphere.id,
      schema_type: createdSphere.properties.type,
      properties: createdSphere.properties,
      parent_id: parentId,
      organization_id: organizationId,
      perspectives: createdSphere.perspectives as any, // GlobalSphere perspectives is Sphere[], TreeMapSphere is string[]
    };

    // Close dialog FIRST
    setAddSphereDialogVisible(false);
    setSelectedSchema(null);
    setParentSphereForCreation(null);

    // Add sphere to local state immediately (no API call needed)
    addSphereToState(treeMapSphere, parentId);

    // Mark this sphere as newly created (persists until TreeMap is reloaded)
    setNewSphereIds((prev) => new Set(prev).add(createdSphere.id));

    // Show success toast
    toastRef.current?.show({
      severity: 'success',
      summary: 'Sphere Created',
      detail: `Created "${createdSphere.properties.name}" successfully`,
      life: 3000,
    });

    // Poll for updates to catch status cascades and flow deployments
    // Include: the new sphere, parent, and ancestors up the hierarchy
    const ancestorIds = getAncestorIds(parentId);
    pollForUpdates([createdSphere.id, parentId, ...ancestorIds], [createdSphere.id]);

    // Notify parent component
    onNodeCreate?.(treeMapSphere);
  };

  // Handle dialog close
  const handleCloseTemplateDialog = () => {
    setTemplateDialogVisible(false);
    setParentSphereForCreation(null);
    setChildColumnConfig(null);
  };

  const handleCloseAddSphereDialog = () => {
    setAddSphereDialogVisible(false);
    setSelectedSchema(null);
  };

  const handleCloseEditSphereDialog = () => {
    setEditSphereDialogVisible(false);
    setSphereToEdit(null);
    setParentSphereForEdit(null);
  };

  // The sphere changed while its editor stays open (its glyph, colour or picture): re-read only that
  // sphere, once. Nothing cascades to its ancestors, so there is nothing to poll for.
  const handleSphereRefresh = React.useCallback(
    (sphereId: string) => {
      void refreshSpheres([sphereId]);
    },
    [refreshSpheres]
  );

  const handleSphereUpdate = async (updatedSphere: GlobalSphere) => {
    // Close dialog FIRST to avoid showing re-renders to the user
    setEditSphereDialogVisible(false);
    setSphereToEdit(null);
    setParentSphereForEdit(null);

    // Show success toast immediately
    toastRef.current?.show({
      severity: 'success',
      summary: 'Sphere Updated',
      detail: `Updated "${updatedSphere.properties.name}" successfully`,
      life: 3000,
    });

    // Update the sphere in local state immediately (no API call needed)
    updateSphereInState(updatedSphere as unknown as TreeMapSphere);

    // Poll for updates to catch status cascades and any async updates
    // Include: the updated sphere and its ancestors
    const ancestorIds = getAncestorIds(updatedSphere.id);
    pollForUpdates([updatedSphere.id, ...ancestorIds], [updatedSphere.id]);

    // Call parent handler if provided
    onNodeUpdate?.(updatedSphere as unknown as TreeMapSphere);
  };

  // Permission list handler for EditSphere
  const fetchPermissionListHandler = React.useCallback(
    async (sphereId: string, nodeLabel: string, permissions?: string[]): Promise<PermissionList | null> => {
      if (!apiClients?.permissionClient) {
        console.error('Permission client not available');
        return null;
      }
      if (!sphereId) {
        console.error('Sphere ID is required to fetch permission list');
        return null;
      }
      if (!nodeLabel) {
        console.error('Node label is required to fetch permission list');
        return null;
      }

      const params = {
        resourceType: nodeLabel,
        resourceId: sphereId,
        permissions,
      };

      try {
        const data = await fetchPermissionList(apiClients.permissionClient, params);
        return data;
      } catch (err) {
        console.error('Error in fetchPermissionListHandler:', err);
        return null;
      }
    },
    [apiClients]
  );

  // Helper: Get eligible schemas for a column based on currently active filters.
  // The "current filters" for eligibility purposes are the composed (context +
  // view + user + session) filters from `useLevelFilters` — the popover's own
  // save path, not a local echo state (EAINT-781 Phase 6).
  const getEligibleSchemasForColumn = React.useCallback((columnIndex: number) => {
    const levelId = resolveLevelIdForColumn(columnIndex);
    const composed = levelId ? levelFilters?.[levelId] : undefined;
    const propertyFilters = composed?.display.propertyFilters ?? [];
    const filterGroups = composed?.display.filterGroups ?? [];

    // Step 1: Start from the ORG-WIDE catalog, not `schemas`.
    //
    // `schemas` is the CREATE-scoped list (deployed approaches on this sphere).
    // On a sphere with no deployments it is empty, so every level resolved to
    // ZERO eligible types -- and a zero-length scope was then treated as "no
    // scope", widening the aspect picker to every type in the org. A column
    // pinned to `center_pillar` offered Identity, Calendar and Orbit aspects
    // that can never match it. Eligibility is a question about what a level can
    // CONTAIN, which is org-scoped exactly like the filter catalog itself.
    let eligibleSchemas = getEligibleSchemaObjects(
      orgFilterSchemas as unknown as typeof schemas,
      {}
    );

    // Step 2: Apply property filters (using module-level helper functions)
    if (propertyFilters.length > 0) {
      const translatedFilters = propertyFilters.map(translateFilter).filter((f: any) => f !== null);
      eligibleSchemas = eligibleSchemas.filter(result => {
        const schema = result.schema || result;
        return translatedFilters.every((filter: any) => checkFilterMatch(schema, filter));
      });
    }

    // Step 3: Apply filterGroups if present
    if (filterGroups.length > 0) {
      const translatedFilterGroups = filterGroups.map((group: any) => ({
        ...group,
        filters: group.filters.map(translateFilter).filter((f: any) => f !== null)
      }));

      eligibleSchemas = eligibleSchemas.filter(result => {
        const schema = result.schema || result;
        return translatedFilterGroups.every((group: any) => {
          if (group.operator === 'or') {
            return group.filters.some((filter: any) => checkFilterMatch(schema, filter));
          } else {
            return group.filters.every((filter: any) => checkFilterMatch(schema, filter));
          }
        });
      });
    }

    // Return schema objects (not wrapped results)
    return eligibleSchemas.map(result => result.schema || result);
  }, [orgFilterSchemas, levelFilters, resolveLevelIdForColumn]);

  // Helper: Get available properties for a column based on eligible schemas
  // Uses the shared buildFilterPropertyOptions helper for consistency across the app
  // Filterable aspects for a column, via the SHARED builder.
  //
  // This used to be ~227 lines of bespoke property assembly fed by `schemas`
  // (the CREATE-scoped list), which returned [] whenever the root sphere had no
  // deployed approaches -- rendering an empty picker with no explanation.
  // `buildFilterPropertyOptions` is the declared canonical builder ("ensures
  // consistent property options across all filter interfaces"); TreeMap already
  // used its sibling for perspectives, so this finishes that migration.
  //
  // Deliberately built from the ORG-WIDE catalog and scoped, not filtered, by
  // the column's eligible types: `eligibleSchemas` tiers the in-scope aspects
  // first while `schemas` keeps everything else findable. A picker that silently
  // omitted out-of-scope aspects could not explain why they were missing.
  const getAvailableProperties = React.useCallback((columnIndex: number): PropertyOption[] => {
    const orgSchemas = orgFilterSchemas as unknown as SchemaForFilters[];
    if (orgSchemas.length === 0) return [];

    // Pass the scope through even when it is empty. Substituting `undefined`
    // here would make the builder default to every org type -- the same silent
    // widening the eligibility bug above produced.
    const eligible = getEligibleSchemasForColumn(columnIndex) as unknown as SchemaForFilters[];
    return buildFilterPropertyOptions({
      schemas: orgSchemas,
      eligibleSchemas: eligible,
      arcTemplates,
      t,
      includeArcProperties: true,
      includeCustomProperties: true,
      groupBy: 'category',
    });
  }, [getEligibleSchemasForColumn, orgFilterSchemas, arcTemplates, t]);

  // Unscoped catalog over every org type, for the ADVANCED editor's rail. It
  // tiers this against the column's own scope while keeping search reaching
  // across all of it, so an aspect on an out-of-scope type is still findable
  // rather than silently absent. Mirrors ContextEditorPage's `orgWideProperties`.
  // Built by `useAspectCatalog` above (EAINT-857 Phase 4) — the same unscoped
  // catalog the Atlas view editor consumes for `propertyOptions`.

  // Resolve a filter's raw stored value to its display label -- e.g. the chip
  // for `type equals "center_pillar"` should read the schema's NAME, not its
  // system_name. Mirrors DataListMolecule's resolver: look the value up in the
  // owning option's `enumValues`, fall back to the raw value.
  //
  // Resolved against the ORG-WIDE catalog rather than the column-scoped one so a
  // chip still renders a real label when the constraint names a type the column
  // itself excludes (which is exactly the case for a context-defined `type`
  // constraint viewed from its own column).
  const getFilterPropertyValueLabel = React.useCallback(
    (propertyKey: string, value: string): string => {
      const option = orgWideFilterProperties.find((p) => p.key === propertyKey);
      const match = option?.enumValues?.find((v) => v.value === value);
      return match?.label ?? value;
    },
    [orgWideFilterProperties]
  );

  const getFilterPropertyLabel = React.useCallback(
    (propertyKey: string): string =>
      orgWideFilterProperties.find((p) => p.key === propertyKey)?.label ?? propertyKey,
    [orgWideFilterProperties]
  );

  // The column's own type scope, as system_names. The advanced editor's rail
  // tiers against this: in-scope types render as expanded sections, everything
  // else is badged out-of-scope but stays searchable. Without it the rail has no
  // notion of scope and renders one undifferentiated list.
  const getScopeTypesForColumn = React.useCallback(
    (columnIndex: number): string[] =>
      getEligibleSchemasForColumn(columnIndex).map((sc: any) => sc.system_name),
    [getEligibleSchemasForColumn]
  );

  // Helper: Get available perspectives for a column
  // Uses the shared buildFilterPerspectiveOptions helper for consistency across the app
  const getAvailablePerspectives = React.useCallback((columnIndex: number): PerspectiveOption[] => {
    // Get all eligible schemas for this column based on current filters
    const eligibleSchemas = getEligibleSchemasForColumn(columnIndex);

    console.log('[TreeMap] getAvailablePerspectives for column', columnIndex, {
      eligibleSchemasCount: eligibleSchemas.length,
      eligibleSchemas: eligibleSchemas.map((s: any) => s.system_name),
    });

    if (eligibleSchemas.length === 0) {
      return [];
    }

    // Use the shared helper to build perspective options
    // TreeMap column filters don't include default Center perspectives - only schema-defined ones
    return buildFilterPerspectiveOptions({
      schemas: eligibleSchemas as SchemaForFilters[],
      t,
      includeDefaultPerspectives: false,
    });
  }, [getEligibleSchemasForColumn, t]);

  // The column's relationships, from the SAME eligible schemas `getAvailableProperties`
  // builds the catalog from: `buildFilterPropertyOptions` offers the Relationship lane iff
  // one of those schemas declares a relationship, so the lane and its options agree by
  // construction. Without this the popover offered the lane and opened an empty picker —
  // the host handed the tree nothing to choose from (EAINT-908 host-frame finding).
  const getAvailableRelationships = React.useCallback((columnIndex: number): RelationshipOption[] => {
    const eligibleSchemas = getEligibleSchemasForColumn(columnIndex);
    if (eligibleSchemas.length === 0) return [];
    return buildFilterRelationshipOptions({ schemas: eligibleSchemas as SchemaForFilters[], t });
  }, [getEligibleSchemasForColumn, t]);


  // Starting level + visible columns (pure derivations). Computed before any early
  // return so the hooks below run unconditionally on every render (Rules of Hooks).
  const startingLevel = navigation.rootColumnLevel || 0;
  // Show all columns - remove the 3-column limit to display the full hierarchy
  const visibleColumns = columnsToRender.slice(startingLevel);

  // First-load state only (context is always provided, check for sphere data loading).
  // Every later fetch renders in place through `loadingColumns`, so a filter edit whose
  // result is empty does not replace the board — and unmount its popover — mid-edit.
  if (isLoading && !hasLoaded) {
    return (
      <div className="tree-map tree-map--loading">
        <div className="tree-map__loading-message">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
          <p>{t('treeMap.loading.data')}</p>
        </div>
      </div>
    );
  }

  // Error state - data loading with error
  if (error) {
    // Determine the error title based on the error content
    const lowerError = error.toLowerCase();
    let errorTitle: string;
    if (lowerError.includes('context') || lowerError.includes('template')) {
      errorTitle = t('treeMap.errors.loadTemplateFailed');
    } else if (lowerError.includes('system_name')) {
      errorTitle = t('treeMap.errors.templateSystemNameRequired');
    } else {
      errorTitle = t('treeMap.errors.loadDataFailed');
    }

    const errorMessage = t('treeMap.errors.tryAgainLater');

    // Only show technical details in development mode
    const isDevelopment = process.env.NODE_ENV === 'development';

    return (
      <div className="tree-map tree-map--error">
        <div className="tree-map__error-message">
          <i className="pi pi-info-circle" style={{ fontSize: '3rem', color: 'var(--info-400)', marginBottom: '1rem' }} />
          <h3>{errorTitle}</h3>
          <p className="tree-map__error-detail">{errorMessage}</p>
          {isDevelopment && <p className="tree-map__error-technical">{error}</p>}
          <ButtonAtom
            label={t('treeMap.errors.retryButton')}
            icon="pi pi-refresh"
            variant="primary"
            onClick={() => globalThis.location.reload()}
            style={{ marginTop: '1rem' }}
          />
        </div>
      </div>
    );
  }

  const headerLeftFallback: React.ReactNode = showBreadcrumbs && navigation.breadcrumbs.length > 0
    ? <TreeMapBreadcrumbs breadcrumbs={navigation.breadcrumbs} onNavigate={navigateToBreadcrumb} />
    : <h2 className="tree-map__title">{context.name?.default || context.name?.translations?.['en-US'] || context.system_name}</h2>;

  return (
    <div className={`tree-map ${className}`} style={style}>
      <Toast ref={toastRef} />

      {/* Header — the context switcher chip lives here as the title (per the
          Context Selector design: the chip IS the title), with breadcrumbs shown
          alongside it once the user drills into a sphere. Falls back to a plain
          title when no contextBar is supplied (TreeMap used without the organism). */}
      <div className="tree-map__header">
        <div className="tree-map__header-left">
          {contextBar ? (
            <>
              {contextBar}
              {showBreadcrumbs && navigation.breadcrumbs.length > 0 && (
                <TreeMapBreadcrumbs
                  breadcrumbs={navigation.breadcrumbs}
                  onNavigate={navigateToBreadcrumb}
                />
              )}
            </>
          ) : headerLeftFallback}
        </div>

        <div className="tree-map__header-right">
          {/* Global AI Button */}
          {showAI && (
            <ButtonAtom
              label={t('treeMap.toolbar.aiAssistant')}
              icon="pi pi-sparkles"
              variant="secondary"
              onClick={() => {
                // Open global AI assistant
                setAIContext({ global: true });
                setAIDialogVisible(true);
              }}
            />
          )}

          {/* View Mode Toggle */}
          {/* Icon-only, mutually exclusive: each needs its own name (a tooltip is not
              one) and the pressed state, in a named group, so a screen reader hears
              "Card view, Minimal view, pressed". */}
          <fieldset className="tree-map__view-toggle" aria-label={t('treeMap.toolbar.cardView')}>
            <ButtonAtom
              icon="pi pi-window-minimize"
              variant={cardView === 'minimal' ? 'primary' : 'secondary'}
              onClick={() => onCardViewChange?.('minimal')}
              tooltip={t('treeMap.toolbar.minimalView')}
              aria-label={t('treeMap.toolbar.minimalView')}
              aria-pressed={cardView === 'minimal'}
            />
            <ButtonAtom
              icon="pi pi-bars"
              variant={cardView === 'standard' ? 'primary' : 'secondary'}
              onClick={() => onCardViewChange?.('standard')}
              tooltip={t('treeMap.toolbar.standardView')}
              aria-label={t('treeMap.toolbar.standardView')}
              aria-pressed={cardView === 'standard'}
            />
            <ButtonAtom
              icon="pi pi-window-maximize"
              variant={cardView === 'expanded' ? 'primary' : 'secondary'}
              onClick={() => onCardViewChange?.('expanded')}
              tooltip={t('treeMap.toolbar.expandedView')}
              aria-label={t('treeMap.toolbar.expandedView')}
              aria-pressed={cardView === 'expanded'}
            />
          </fieldset>
        </div>
      </div>

      {/* Columns or Grid */}
      <div className="tree-map__content">
        {/* New Grid Layout with Children Grids */}
        {useGridLayout && layout ? (
          <TreeMapGrid
            layout={layout}
            template={templateShim as any}
            rootTemplateLevel={rootTemplateLevel}
            editable={editable}
            baseConfig={baseConfig}
            viewConfig={viewConfig}
            columnWidth={viewConfig?.columnWidth}
            paginationState={paginationState}
            collapsedNodeIds={renderCollapsedNodeIds}
            hiddenColumns={hiddenColumns}
            loadingColumns={loadingColumns}
            newSphereIds={newSphereIds}
            onNodeSelect={onNodeSelect}
            onNodeExpand={handleExpand}
            onNodeCreate={handleNodeCreate}
            onNodeUpdate={handleNodeUpdateInternal}
            onNodeDelete={onNodeDelete}
            onToggleCollapse={handleToggleCollapse}
            onToggleColumnVisibility={handleToggleColumnVisibility}
            onColumnWidthChange={(levelId, width) => {
              onBoardStateChange?.({ columnState: { [levelId]: { width } } });
            }}
            onFocus={(sphereId: string) => {
              focusOnSphere(sphereId);
            }}
            onLoadMore={async (sphereId: string, _level: number) => {
              // Find the sphere across all columns (parent might be in different column than children)
              let sphere = null;
              for (const col of Object.keys(reorganizedSpheres)) {
                const found = reorganizedSpheres[Number(col)]?.find(s => s.id === sphereId);
                if (found) {
                  sphere = found;
                  break;
                }
              }

              if (sphere?.properties?.type) {
                await loadMoreChildren(sphereId, sphere.properties.type);
              } else {
                console.warn('⚠️ [TreeMap] Could not find sphere or type:', { sphereId, sphere });
              }
            }}
            schemas={schemas}
            sphereLookup={sphereLookup}
            levelFilters={levelFilters}
            filterSaveTargets={filterSaveTargets}
            onSetLevelFilter={onSetLevelFilter}
            linkSuppressed={linkSuppressed}
            onExitSharedLink={onExitSharedLink}
            canEditContext={canEditContext}
            onEditInContext={onEditContext}
            contextSystemName={context.system_name}
            getAvailableProperties={getAvailableProperties}
            orgWideFilterProperties={orgWideFilterProperties}
            getFilterPropertyLabel={getFilterPropertyLabel}
            getScopeTypesForColumn={getScopeTypesForColumn}
            filterFamilyTypeSchemas={orgFilterSchemas}
            getFilterPropertyValueLabel={getFilterPropertyValueLabel}
            filterPropertiesLoading={orgFilterSchemasLoading}
            filterPropertiesError={orgFilterSchemasError}
            getAvailablePerspectives={getAvailablePerspectives}
            getAvailableRelationships={getAvailableRelationships}
            filterPerspectiveSearchProps={{
              direction: 'outwards',
              hops: 2,
            }}
            allSchemas={orgFilterSchemas as unknown as SchemaForFilters[]}
            onShareWithView={onShareWithView}
          />
        ) : (
          /* Old Column-based Layout */
          <>
            <div className="tree-map__columns">
              {/* Column 0: Root Sphere (always shown) - Special display without swimlanes */}
              {(() => {
                const shouldRenderColumn0 = reorganizedSpheres[0] && reorganizedSpheres[0].length > 0;

                if (!shouldRenderColumn0) {
                  return null;
                }

                return (
                  <div className="tree-map-column">
                    <div className="tree-map-column__header">
                      <div className="tree-map-column__title">
                        <i className="pi pi-bullseye tree-map-column__icon" />
                        <h3>Focus</h3>
                      </div>
                    </div>
                    <div className="tree-map-column__content">
                      <div className="tree-map-swimlane tree-map-swimlane--root tree-map-swimlane--expanded">
                        <div className="tree-map-swimlane__content">
                          {reorganizedSpheres[0].map((sphere) => (
                              <TreeMapCard
                                key={sphere.id}
                                sphere={sphere}
                                level={{
                                  id: 'focus',
                                  order: -1,
                                  label: { default: 'Focus' },
                                  direction: 'outwards',
                                } as ContextLevel}
                                viewConfig={viewConfig}
                                baseConfig={baseConfig}
                                cardView={cardView}
                                editable={false}
                                isSelected={selectedSphereIds.includes(sphere.id)}
                                isDragging={false}
                                onSelect={() => handleSphereSelect(sphere.id)}
                                onExpand={undefined}
                                onEdit={undefined}
                                onDelete={undefined}
                                onDragStart={undefined}
                                onDragEnd={undefined}
                                schemas={schemas}
                                sphereLookup={sphereLookup}
                              />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Template-driven columns (Column 1+) */}
              {visibleColumns.map((level, index) => {
                const templateLevel = startingLevel + index;
                const columnIndex = templateLevel + 1;  // Offset by 1 for root column
                const spheresForColumn = reorganizedSpheres[columnIndex] || [];

                // Get parent spheres from previous column
                const parentSpheres = reorganizedSpheres[columnIndex - 1] || [];

                return (
                  <TreeMapColumn
                    key={level.id || templateLevel}
                    level={level}
                    levelIndex={templateLevel}
                    viewConfig={viewConfig}
                    baseConfig={baseConfig}
                    spheres={spheresForColumn}
                    parentSpheres={parentSpheres as any}
                    editable={editable}
                    onExpand={() => handleColumnExpand(templateLevel)}
                    onSphereSelect={handleSphereSelect}
                    onSphereDrop={(sphereId, targetParentId) => {
                      // Handle drop
                      moveSphere(sphereId, targetParentId);
                    }}
                    cardView={cardView}
                    organizationId={organizationId}
                    schemas={schemas}
                    apiClient={apiClients?.graphClient || null}
                    defaultPageSize={effectivePageSize}
                    contextSystemName={context.system_name}
                    rootSphereId={rootSphereId}
                  />
                );
              })}
            </div>

            {/* Empty state - no levels */}
            {visibleColumns.length === 0 && (
              <div className="tree-map__empty">
                <i className="pi pi-inbox" style={{ fontSize: '3rem' }} />
                <h3>No levels defined</h3>
                <p>This template doesn't have any levels configured.</p>
              </div>
            )}

            {/* Empty state - no data */}
            {visibleColumns.length > 0 && !isLoading && Object.keys(spheresByColumn).length === 0 && (
              <div className="tree-map__empty">
                <i className="pi pi-inbox" style={{ fontSize: '3rem' }} />
                <h3>No spheres found</h3>
                <p>No spheres match the current template configuration or the root sphere has no children.</p>
              </div>
            )}

            {/* Empty state - data loaded but all columns empty */}
            {visibleColumns.length > 0 && !isLoading && Object.keys(spheresByColumn).length > 0 &&
            Object.values(spheresByColumn).every(spheres => spheres.length === 0) && (
              <div className="tree-map__empty">
                <i className="pi pi-inbox" style={{ fontSize: '3rem' }} />
                <h3>No spheres to display</h3>
                <p>The selected sphere has no children matching the template criteria.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* AI Assistant Dialog */}
      <CustomDialog
        visible={aiDialogVisible}
        onHide={() => setAIDialogVisible(false)}
        header={aiContext?.global ? 'AI Assistant' : `AI: Suggest ${aiContext?.level?.label}`}
        style={{ width: '600px' }}
      >
        {aiContext && (
          <TreeMapAIAssistant
            context={aiContext}
            onSuggestionAccept={handleSuggestionAccept}
            onSuggestionCustomize={(suggestion) => {
              // Open sphere editor with pre-filled data
              setAIDialogVisible(false);
            }}
            onClose={() => setAIDialogVisible(false)}
          />
        )}
      </CustomDialog>

      {/* AddTemplate Dialog */}
      {parentSphereForCreation && (
        <DialogWrapper
          header="Select Template"
          visible={templateDialogVisible}
          onHide={handleCloseTemplateDialog}
          style={{ width: '85vw', maxWidth: '1600px', height: '80vh' }}
        >
          <AddTemplate
            assigneeSystemName={parentSphereForCreation.properties.type}
            assigneeNodeLabel={parentSphereForCreation.label}
            perspectiveSystemNames={
              Array.isArray(parentSphereForCreation.perspectives)
                ? parentSphereForCreation.perspectives.map((p: any) => p.properties.system_name)
                : Object.keys(parentSphereForCreation.perspectives || {})
            }
            propertyFilters={childColumnConfig?.filters?.propertyFilters}
            filterGroups={childColumnConfig?.filters?.filterGroups}
            onUseTemplate={handleSchemaSelect}
            showPreviewButton={true}
            onSphereTypeSelect={(_sphereType, _source, _label) => {
              // Handler for sphere type selection - no action needed
            }}
          />
        </DialogWrapper>
      )}

      {/* AddSphere Dialog */}
      {selectedSchema && parentSphereForCreation && (() => {
        // Convert TreeMapSphere to GlobalSphere format for AddSphere
        const globalParentSphere: GlobalSphere = {
          id: parentSphereForCreation.id,
          label: parentSphereForCreation.properties.type || '',
          properties: {
            type: parentSphereForCreation.properties.type || '',
            name: parentSphereForCreation.properties.name || '',
            ...parentSphereForCreation.properties,
          },
          perspectives: parentSphereForCreation.perspectives as any,
          assignedPerspectives: parentSphereForCreation.assignedPerspectives || [],
        };

        // Get localized schema name
        const schemaNameKey = selectedSchema.localization_name_key;
        const schemaName = schemaNameKey
          ? t(schemaNameKey.replace(/^schema\./, ''), { defaultValue: selectedSchema.name })
          : selectedSchema.name;

        const headerContent = (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GlyphAtom glyph={selectedSchema.glyph} glyphColor={selectedSchema.glyph_color ?? undefined} />
            <span>{t('createSphere', { ns: 'schema', defaultValue: 'Create {{sphereType}}', sphereType: schemaName })}</span>
          </div>
        );

        return (
          <CustomDialog
            header={headerContent}
            visible={addSphereDialogVisible}
            onHide={handleCloseAddSphereDialog}
            style={{ width: '90vw', maxWidth: '1800px' }}
          >
            <AddSphere
              schema={selectedSchema}
              parentSphere={globalParentSphere}
              onSave={handleSphereSave}
              onClose={handleCloseAddSphereDialog}
            />
          </CustomDialog>
        );
      })()}

      {/* EditSphere Dialog */}
      {sphereToEdit && parentSphereForEdit && (() => {
        // Convert TreeMapSphere to GlobalSphere format for EditSphere
        // TreeMapSphere and GlobalSphere are compatible, just need type assertion
        const globalSphere = sphereToEdit as unknown as GlobalSphere;
        const globalParentSphere = parentSphereForEdit as unknown as GlobalSphere;

        return (
          <DialogWrapper
            header="Edit Sphere"
            visible={editSphereDialogVisible}
            onHide={handleCloseEditSphereDialog}
            style={{ width: '85vw', maxWidth: '1200px', height: '80vh' }}
          >
            <EditSphere
              sphere={globalSphere}
              parentSphere={globalParentSphere}
              onSave={handleSphereUpdate}
              onSphereRefresh={handleSphereRefresh}
              onClose={handleCloseEditSphereDialog}
              fetchPermissionList={fetchPermissionListHandler}
            />
          </DialogWrapper>
        );
      })()}
    </div>
  );
};

export default TreeMap;
